(ns common.schema.entities.workspace.kyc.kyc-process
  (:require
    [common.languages :as languages]
    [common.schema.entities.kyc.options :as kyc-options]
    [common.schema.entities.workspace.kyc.kyc-utils :as ku]
    [common.schema.entities.workspace.kyc.templates.template-registry :as template-registry]
    [common.schema.schema-utils :as su]
    [common.util :as cu]))

(defn template-option-filter
  [{:keys [_search-str-lc
           containing-entity
           entity-candidate
           _has-focus?
           entity-types-lookup]
    :as   _filter-args}]
  (let [containing-entity-tenant-id (when-let [containing-tenant (:common/tenant containing-entity)]
                                      (if (number? containing-tenant)
                                        containing-tenant
                                        (:db/id containing-tenant)))
        tenant (->> (entity-types-lookup :entity.type/tenant)
                    (cu/filter-by :db/id containing-entity-tenant-id)
                    (first))
        kyc-profile-entity-type
        (:kyc-process/containing-entity-type containing-entity)

        templates-attr (case kyc-profile-entity-type
                         :entity.type/tenant-kyc-profile :tenant/active-tenant-kyc-templates
                         :entity.type/contract-kyc-profile :tenant/active-contract-kyc-templates
                         :tenant/active-kyc-templates)

        active-templates (get tenant templates-attr)]
    (contains? (set active-templates) (:key entity-candidate))))

(def template-options
  (->> template-registry/templates
       (mapv (fn [[k schema]]
               {:key   k
                :label (:kyc-template/template-name schema)}))))

(defn template-label
  [template-kw]
  (->> template-options
       (filter (fn [to]
                 (= (:key to) template-kw)))
       (first)
       :label))

(def template-language-options
  (->> languages/options
       (filter (fn [opt]
                 (some #{(:key opt)} [:ENG :FIN])))))

(def containing-entity-type-options
  [{:key   :entity.type/kyc-profile
    :label "KYC profile"}
   {:key   :entity.type/tenant-kyc-profile
    :label "Tenant KYC profile"}
   {:key   :entity.type/contract-kyc-profile
    :label "Contract KYC profile"}])

(def schema
  (vec
   (concat
    (su/single-ref-field :kyc-process/inviter
                         "Inviter"
                         [:entity.type/account]
                         {:authorization su/access-user-user
                          :default-field-visibility :in-use-disabled})
    (su/email-field :kyc-process/invitee-email "Invitee email" {:authorization su/access-user-user
                                                                :default-field-visibility :in-use-disabled})
    (su/text-simple-field :kyc-process/invitee-phone
                          "Invitee phone"
                          {:authorization su/access-user-user
                           :default-field-visibility :in-use-disabled})
    (su/keyword-field :kyc-process/status
                      "Status"
                      kyc-options/process-status-options
                      {:authorization su/access-user-ssg
                       :default-field-visibility :in-use-disabled})
    (su/keyword-field :kyc-process/template
                      "Template"
                      template-options
                      {:authorization    su/access-user-ssg
                       :option-filter-fn template-option-filter
                       :default-field-visibility :in-use-disabled})
    (su/keyword-field :kyc-process/language
                      "Language"
                      template-language-options
                      {:authorization su/access-user-ssg
                       :default-field-visibility :in-use-disabled})
    (su/single-ref-field :kyc-process/target
                         "Target"
                         [:entity.type/investor :entity.type/company]
                         {:authorization su/access-user-user
                          :default-field-visibility :in-use-disabled})
    (su/text-edn-field :kyc-process/answers "Answers" {:authorization su/access-ssg-ssg
                                                       :default-field-visibility :in-use-disabled})
    (su/single-ref-field :kyc-process/signed-document
                         "Signed document"
                         [:entity.type/attachment]
                         {:authorization su/access-ssg-ssg
                          :default-field-visibility :in-use-disabled})
    (su/datetime-field :kyc-process/approval-date "Process approval date" {})
    (su/datetime-field :kyc-process/invited-date
                       "Process invitation time"
                       {:authorization su/access-system-user
                        :default-field-visibility :in-use-disabled
                        :system-semantics su/system-controlled-optional-field-semantics})

    (su/datetime-field :kyc-process/first-reminder-sent-on
                       "First reminder sent on"
                       {:authorization su/access-system-user
                        :default-field-visibility :in-use
                        :system-semantics su/system-controlled-optional-field-semantics})

    (su/datetime-field :kyc-process/second-reminder-sent-on
                       "Second reminder sent on"
                       {:authorization su/access-system-user
                        :default-field-visibility :in-use
                        :system-semantics su/system-controlled-optional-field-semantics})

    (su/keyword-field :kyc-process/containing-entity-type
                      "Containing entity type"
                      containing-entity-type-options
                      {:authorization su/access-user-ssg
                       :default-field-visibility :in-use}))))

(defn board-member-answers
  [board-members confirmation-status]
  (let [confirmation-status (if (keyword? confirmation-status)
                              confirmation-status
                              :pending)]
    (->> board-members
         (map (fn [bm]
                (-> bm
                    (assoc :kyc-board-member/last-confirmation-status
                           (:kyc-board-member/confirmation-status bm))
                    (assoc :kyc-board-member/confirmation-status confirmation-status)))))))

(defn- strip-deprecated-value
  "If `reply` references any `:deprecated?` option, return nil so the field
   is dropped on copy. For multi-select values, strips just the deprecated
   members; if nothing remains, returns nil."
  [reply options]
  (let [deprecated (ku/deprecated-option-keys options)]
    (cond
      (empty? deprecated)
      reply

      (or (set? reply) (sequential? reply))
      (let [filtered (vec (remove deprecated reply))]
        (when (seq filtered)
          (if (set? reply) (set filtered) filtered)))

      (contains? deprecated reply)
      nil

      :else reply)))

(defn copy-answers
  [{:keys [answers template-schema opts]}]
  (let [{:keys [skip-kws]} opts]
    (some->>
     template-schema
     (mapcat
      (fn [[_ section-meta]]
        (->>
         section-meta
         :questions
         (remove (fn [[_ q-meta]]
                   (and skip-kws
                        (:skip-previous-answers-copy? q-meta))))
         (keep (fn [[question-kw question-meta]]
                 (let [raw-reply (get answers question-kw)
                       options (:options question-meta)
                       reply (strip-deprecated-value raw-reply options)]
                   (when (or (some? reply)
                             (nil? raw-reply))
                     (let [data-type (:data/type question-meta)
                           data-template (:data/template question-meta)
                           coerced-reply (let [edn-type (:edn-data/type question-meta)]
                                           (cond
                                             ;; Special handling for board members to set confirmation statuses
                                             (= edn-type :edn-data.type/board-members-list)
                                             (board-member-answers reply (:board-member-confirmation-status opts))

                                             ;; Generic deep-copy for text-edn fields with a nested data template,
                                             ;; excluding types that must be preserved as-is (UBO/PEP lists)
                                             (and (= :data.type/text-edn data-type)
                                                  data-template
                                                  (not (some #{edn-type}
                                                             [:edn-data.type/pep-list])))
                                             (cond
                                               (or (vector? reply)
                                                   (list? reply))
                                               (->> reply
                                                    (map-indexed (fn [idx item]
                                                                   (-> (copy-answers {:answers         item
                                                                                      :template-schema data-template
                                                                                      :opts            opts})
                                                                       (assoc :ord (or (:ord item) idx)))))
                                                    (vec))

                                               (map? reply)
                                               (copy-answers {:answers         reply
                                                              :template-schema data-template
                                                              :opts            opts})

                                               :else reply)
                                             (and (= :data.type/text-edn data-type)
                                                  data-template
                                                  (some #{edn-type}
                                                        [:edn-data.type/ubo-list]))
                                             (do (println "UBO : " reply)
                                                 reply)

                                             :else reply))]
                       [question-kw coerced-reply]))))))))
     (into {}))))

(defn copy-process-answers
  ([process]
   (copy-process-answers process {}))
  ([process opts]
   (let [template-kw (:kyc-process/template process)
         template-schema (template-registry/templates template-kw)
         answers (:kyc-process/answers process)]
     (copy-answers
      {:answers         answers
       :template-schema template-schema
       :opts            opts}))))
